// react
import React, { useState, useMemo } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  IconButton,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, Edit as EditIcon } from "@mui/icons-material";

// config
import documentTypeLabels from "../../../config/documentTypeLabels.json";

// A ---------------------------------------------------------------------- M

const getDocumentTypeLabel = (code) => {
  return documentTypeLabels[code] || code;
};

const ClientPoliciesToolbar = () => {
  const { t } = useTranslation();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={7}>
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
          {t("policies")}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ClientPoliciesHead = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const headCells = useMemo(
    () => [
      { id: "tipologiaDocumentale", label: t("document_type") },
      { id: "condition", label: t("condition") },
      { id: "actions", label: t("actions") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align={headCell.id !== "actions" ? "left" : "right"} sx={{ fontWeight: 700 }}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const DataRow = ({ agreement, clickEdit }) => {
  const { t } = useTranslation();

  const conditionDisplay = agreement.condition === "end_of_month" ? t("Monthly") : agreement.condition === "end_of_year" ? t("Yearly") : agreement.condition || "N/A";

  return (
    <TableRow>
      <TableCell>{agreement.tipologiaDocumentale ? getDocumentTypeLabel(agreement.tipologiaDocumentale) : agreement.default === true ? "DEFAULT ALL TYPES - ID_POLICY_67890" : "N/A"}</TableCell>
      <TableCell>{conditionDisplay}</TableCell>
      <TableCell align="right" size="small">
        <Tooltip title={t("edit")} placement="top">
          <IconButton color="primary" onClick={() => clickEdit(agreement)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const CustomerPoliciesTable = ({ agreements, clickEdit }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - agreements.length) : 0;

  return (
    <Box>
      <ClientPoliciesToolbar />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
          backdropFilter: "blur(6px)",
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table>
          {!isMobile && <ClientPoliciesHead />}

          <TableBody>
            {(rowsPerPage > 0 ? agreements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : agreements).map((agreement) => {
              return <DataRow key={agreement.id} agreement={agreement} clickEdit={clickEdit} />;
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>

          {!isMobile && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={agreements.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

CustomerPoliciesTable.propTypes = {
  agreements: PropTypes.array.isRequired,
  clickEdit: PropTypes.func.isRequired,
};

export default CustomerPoliciesTable;
